import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import styled from "styled-components";
import Papa from "papaparse";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { api } from "../../api/axios";

export default function AdminBatchUploadModal({
  show,
  onHide,
  session,
  termName,
  onSuccess,
}) {
  const [file, setFile] = useState(null);
  const [values, setValues] = useState([]);
  const [waiting, setWaiting] = useState(false);
  const allowedExtension = ["csv"];

  const handleCsvUpload = (event) => {
    const incomingFile = event.target.files[0];
    if (!incomingFile) return;

    const fileExtension = incomingFile.name.split(".").pop();
    setFile(incomingFile);

    if (!allowedExtension.includes(fileExtension.toLowerCase())) {
      toast.error("Please upload a valid CSV file");
    } else {
      Papa.parse(incomingFile, {
        header: false,
        skipEmptyLines: true,
        complete: function (result) {
          const headerRow = result.data[0];
          if (headerRow) {
            let valuesArray = result.data.map((row) => Object.values(row));
            // Remove rows where the first cell (admission number) is empty
            valuesArray = valuesArray.filter((row) => row[0] !== "");

            // Note: We include the header row in the payload because the templates
            // often have multiple header rows or data spanning rows, and the backend/rendering
            // just treats the whole matrix as `results: any[][]`.
            setValues(valuesArray);
          }
        },
        error: (err) => {
          toast.error("Failed to parse CSV file");
        },
      });
    }
  };

  const handleSubmit = async () => {
    if (!values.length || !session || !termName) {
      toast.error(
        "Please ensure you have selected a Term, Session, and uploaded a valid CSV.",
      );
      return;
    }

    setWaiting(true);
    try {
      const payload = {
        results: values,
        term: termName,
        currentSession: session,
      };

      const response = await api.post("/results/saveBatchResults", payload);
      setWaiting(false);
      toast.success(
        `Batch Results processed successfully across ${response.data.classesAffected?.length || 0} classes.`,
      );
      setFile(null);
      setValues([]);
      if (onSuccess) onSuccess();
      onHide();
    } catch (error) {
      console.log(error);
      setWaiting(false);
      toast.error(
        error.response?.data?.message || "Unable to upload batch results.",
      );
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Admin Batch Results Upload</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <p className="text-muted m-0">
            Upload a single massive CSV containing the results for an entire
            school section (e.g., all students in Junior School). The system
            will read each student's Admission Number, query their exact class,
            and group the results correctly in the database.
          </p>
          <hr />
          <ul className="text-muted small">
            <li>
              <strong>Target Session:</strong> {session || "Not Selected"}
            </li>
            <li>
              <strong>Target Term:</strong> {termName || "Not Selected"}
            </li>
          </ul>
        </div>

        <Wrapper className="d-flex flex-column justify-content-center align-items-center p-4">
          <div className="upload-area position-relative d-flex flex-column justify-content-center align-items-center gap-2 px-3">
            <Icon className="icon" icon="fa6-solid:cloud-arrow-up" />
            {file ? (
              <p className="fw-bold mt-2">
                {file.name} ({values.length} rows parsed)
              </p>
            ) : (
              <>
                <p className="text-muted">Drag file here or browse for file.</p>
                <input
                  type="file"
                  name="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  style={{
                    cursor: "pointer",
                    left: 0,
                    right: 0,
                    width: "fit-content",
                  }}
                />
              </>
            )}

            {file && (
              <button
                className="btn btn-primary mt-3 text-white submit d-flex justify-content-center align-items-center"
                onClick={handleSubmit}
                disabled={waiting}
              >
                {waiting ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  "Process Batch Upload"
                )}
              </button>
            )}
          </div>
        </Wrapper>
      </Modal.Body>
    </Modal>
  );
}

const Wrapper = styled.div`
  .upload-area {
    min-height: 270px;
    min-width: 250px;
    border: 1px dashed grey;
    border-radius: 20px;
    width: 60%;
  }
  .icon {
    font-size: 60px !important;
    color: grey;
  }
  input {
    width: 220px !important;
    cursor: pointer;
  }
`;
